import {makeAutoObservable} from "mobx";
import {FmsReceiverFormData} from "../../components/page/fms/FmsReceiverForm";
import {ShipmentPackageUpdateReceiverDto} from "../../api/fms";
import {api, catchApi} from "../../api";
import {validationStore} from "../ValidationStore";


class FmsReceiverStore {
    get packageId(): string {
        return this._packageId;
    }

    set packageId(value: string) {
        this._packageId = value;
    }

    get version(): number {
        return this._version;
    }

    set version(value: number) {
        this._version = value;
    }

    get executing(): boolean {
        return this._executing;
    }

    set executing(value: boolean) {
        this._executing = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    set valid(value: boolean) {
        this._valid = value;
    }

    get country(): string {
        return this._country;
    }

    set country(value: string) {
        this._country = value;
    }

    get houseNumber(): string {
        return this._houseNumber;
    }

    set houseNumber(value: string) {
        this._houseNumber = value;
    }

    get postCode(): string {
        return this._postCode;
    }

    set postCode(value: string) {
        this._postCode = value;
    }

    get street(): string {
        return this._street;
    }

    set street(value: string) {
        this._street = value;
    }

    get line1(): string {
        return this._line1;
    }

    set line1(value: string) {
        this._line1 = value;
    }

    get line2(): string {
        return this._line2;
    }

    set line2(value: string) {
        this._line2 = value;
    }

    get mail(): string {
        return this._mail;
    }

    set mail(value: string) {
        this._mail = value;
    }

    get phone(): string {
        return this._phone;
    }

    set phone(value: string) {
        this._phone = value;
    }

    get addition(): string {
        return this._addition;
    }

    set addition(value: string) {
        this._addition = value;
    }

    get city(): string {
        return this._city;
    }

    set city(value: string) {
        this._city = value;
    }

    private _valid: boolean = false
    private _executing: boolean = false

    private _packageId: string = ''
    private _version: number = -1
    private _city: string = ''
    private _addition: string = ''
    private _country: string = ''
    private _houseNumber: string = ''
    private _postCode: string = ''
    private _street: string = ''
    private _line1: string = ''
    private _line2: string = ''
    private _mail: string = ''
    private _phone: string = ''

    constructor() {
        makeAutoObservable(this)
    }

    get formData(): FmsReceiverFormData {
        const {
            packageId,
            version,
            city,
            addition,
            country,
            houseNumber,
            postCode,
            street,
            line1,
            line2,
            mail,
            phone,
        } = this

        return {
            packageId,
            version,
            city,
            addition,
            country,
            houseNumber,
            postCode,
            street,
            line1,
            line2,
            mail,
            phone,
        }
    }

    set formData(data: FmsReceiverFormData) {
        const {
            packageId,
            version,
            city,
            addition,
            country,
            houseNumber,
            postCode,
            street,
            line1,
            line2,
            mail,
            phone,
        } = data
        this.packageId = packageId
        this.version = version
        this.city = city
        this.addition = addition
        this.country = country
        this.houseNumber = houseNumber
        this.postCode = postCode
        this.street = street
        this.line1 = line1
        this.line2 = line2
        this.mail = mail
        this.phone = phone
    }

    async update(): Promise<boolean> {
        this._executing = true
        await this.validate()
        if (this.valid && this.packageId) {
            const data = this.build()
            await catchApi(async () => await api.fms.api.updateReceiver(this.packageId, data))
            this._executing = false
            this.reset()
            return true
        }
        this._executing = false
        return false
    }

    async validate() {
        this.valid = false
        const dto = this.build()
        if (!dto) {
            return
        }
        await validationStore.validate(api.fms.api.validateShipmentPackageReceiverUpdateDto, dto)
        this.valid = Boolean(validationStore.valid)
    }

    public build(): ShipmentPackageUpdateReceiverDto {
        const {
            version,
            city,
            addition,
            country,
            houseNumber,
            postCode,
            street,
            line1,
            line2,
            mail,
            phone,
        } = this

        return {
            version: version,
            receiver: {
                line1,
                line2: line2 || undefined,
                mail: mail || undefined,
                phone: phone || undefined,
                address: {
                    city,
                    addition: addition || undefined,
                    country,
                    houseNumber: houseNumber || undefined,
                    postCode,
                    street,
                }
            }
        }
    }

    reset(validate?: boolean) {
        this.packageId = ''
        this.version = -1
        this.city = ''
        this.addition = ''
        this.country = ''
        this.houseNumber = ''
        this.postCode = ''
        this.street = ''
        this.line1 = ''
        this.line2 = ''
        this.mail = ''
        this.phone = ''
        if (validate) {
            this.validate().finally()
        }
    }
}

const fmsReceiverStore = new FmsReceiverStore()

export {
    fmsReceiverStore
}
